/**
 * ParlayBadges Component
 * Badge sub-components for tracked parlay cards
 */

import { PARLAY_TIERS, type TierConfig } from '@config/constants';
import { ORIGIN_BADGES } from '@parlay-picker/shared/badgeConfig';
import { LEAGUE_HELD_NO_SIGNAL_DESCRIPTION } from '@parlay-picker/shared/mlRecommendation';
import { getParlayLabel, normalizeScoreForDisplay } from '@parlay-picker/shared/scoreDisplay';
import type { ReactElement } from 'react';
import { RESULT_STYLES, TIER_ICONS, TIER_STYLES } from './utils';

// =============================================================================
// TierBadge
// =============================================================================

interface TierBadgeProps {
  tier: string;
}

export function TierBadge({ tier }: TierBadgeProps): ReactElement {
  const tierInfo: TierConfig | undefined = PARLAY_TIERS[tier];
  const style = TIER_STYLES[tier] || TIER_STYLES.Custom;
  const IconComponent = tierInfo ? TIER_ICONS[tierInfo.icon] : undefined;

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${style}`}
      title={tierInfo?.description}
    >
      {IconComponent && <IconComponent className="w-3 h-3" />} {tier}
    </span>
  );
}

// =============================================================================
// ParlayResultBadge
// =============================================================================

interface ParlayResultBadgeProps {
  result?: string;
}

export function ParlayResultBadge({ result }: ParlayResultBadgeProps): ReactElement {
  const style = RESULT_STYLES[result || ''] || RESULT_STYLES.pending;
  const label = result?.toUpperCase() || 'PENDING';
  return <span className={`px-2 py-0.5 rounded text-xs font-bold ${style}`}>{label}</span>;
}

// =============================================================================
// OriginBadge
// =============================================================================

interface OriginBadgeProps {
  origin: 'ai' | 'manual' | 'bet';
}

export function OriginBadge({ origin }: OriginBadgeProps): ReactElement | null {
  const badge = ORIGIN_BADGES[origin];
  if (!badge) return null;

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
      {badge.label}
    </span>
  );
}

// =============================================================================
// ScoreBadge
// =============================================================================

interface ScoreBadgeProps {
  type: 'user' | 'ai';
  score: number | null | undefined;
  /** Raw thresholds for normalization. If not provided, uses 2-leg defaults. */
  thresholds?: { bet: number; skip: number };
  /** False shows BET and LEAN as NO SIGNAL; absent keeps the score's own label. */
  isLabelTrusted?: boolean | null;
}

export function ScoreBadge({
  type,
  score,
  thresholds,
  isLabelTrusted,
}: ScoreBadgeProps): ReactElement | null {
  const icon = type === 'user' ? '💰' : '🎯';
  const label = type === 'user' ? 'Your Score' : 'AI Score';

  if (score == null) return null;

  const displayScore = normalizeScoreForDisplay(score, thresholds);
  const { label: displayRec, color } = getParlayLabel(displayScore, isLabelTrusted);

  const bgColor =
    color === 'success'
      ? 'bg-theme-success/20 border-theme-success/40'
      : color === 'lean'
        ? 'bg-teal-500/20 border-teal-400/40'
        : color === 'caution'
          ? 'bg-theme-warning/20 border-theme-warning/40'
          : color === 'none'
            ? 'bg-slate-500/20 border-slate-400/40'
            : 'bg-theme-danger/20 border-theme-danger/40';

  const textColor =
    color === 'success'
      ? 'text-theme-success'
      : color === 'lean'
        ? 'text-teal-400'
        : color === 'caution'
          ? 'text-theme-warning'
          : color === 'none'
            ? 'text-slate-400'
            : 'text-theme-danger';

  const recLabel = displayRec === 'LEAN BET' ? 'LEAN' : displayRec;

  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded border ${bgColor} ${textColor} font-medium`}
      title={displayRec === 'NO SIGNAL' ? `${label}: ${LEAGUE_HELD_NO_SIGNAL_DESCRIPTION}` : label}
      data-testid={`score-badge-${type}`}
    >
      {icon} {displayScore} {recLabel}
    </span>
  );
}

// =============================================================================
// SgpBadge
// =============================================================================

export function SgpBadge(): ReactElement {
  return (
    <span
      className="text-[10px] px-1.5 py-0.5 rounded border font-semibold bg-theme-secondary/15 text-theme-secondary border-theme-secondary/30"
      title="Same-Game Parlay — all legs from one game"
    >
      SGP
    </span>
  );
}

// =============================================================================
// LiveBadge
// =============================================================================

interface LiveBadgeProps {
  liveGamesCount: number;
}

export function LiveBadge({ liveGamesCount }: LiveBadgeProps): ReactElement {
  return (
    <span
      className="text-[10px] px-1.5 py-0.5 rounded font-bold animate-pulse bg-theme-danger text-white"
      title={`${liveGamesCount} game${liveGamesCount !== 1 ? 's' : ''} in progress`}
    >
      LIVE
    </span>
  );
}
